/**
 * EnvironmentVariablesBuilder Component
 *
 * Component for managing application environment variables (key-value pairs).
 * Keys must be uppercase with letters, numbers, and underscores.
 * Values can be any string up to 4096 characters.
 */

import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { EnvironmentVariable } from '../../../types/application.types';

interface EnvironmentVariablesBuilderProps {
  variables: EnvironmentVariable[];
  onChange: (variables: EnvironmentVariable[]) => void;
  disabled?: boolean;
  error?: string;
}

/**
 * Validate environment variable key format
 * Must be uppercase, start with a letter, and contain only letters, numbers, and underscores
 */
const isValidKey = (key: string): boolean => {
  return /^[A-Z][A-Z0-9_]*$/.test(key);
};

export const EnvironmentVariablesBuilder: React.FC<EnvironmentVariablesBuilderProps> = ({
  variables,
  onChange,
  disabled = false,
  error,
}) => {
  const [newKey, setNewKey] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [keyError, setKeyError] = useState<string>('');

  // Add new environment variable
  const addVariable = () => {
    const trimmedKey = newKey.trim().toUpperCase();
    const trimmedValue = newValue.trim();

    setKeyError('');

    if (!trimmedKey) {
      setKeyError('Key is required');
      return;
    }

    if (!isValidKey(trimmedKey)) {
      setKeyError('Key must be uppercase, start with a letter, and contain only letters, numbers, and underscores');
      return;
    }

    if (variables.some((v) => v.key === trimmedKey)) {
      setKeyError('This key already exists');
      return;
    }

    if (trimmedValue.length > 4096) {
      setKeyError('Value must not exceed 4096 characters');
      return;
    }

    onChange([...variables, { key: trimmedKey, value: trimmedValue }]);
    setNewKey('');
    setNewValue('');
  };

  // Remove environment variable
  const removeVariable = (keyToRemove: string) => {
    onChange(variables.filter((v) => v.key !== keyToRemove));
  };

  // Update existing variable value
  const updateVariable = (key: string, newValue: string) => {
    onChange(
      variables.map((v) =>
        v.key === key ? { ...v, value: newValue } : v
      )
    );
  };

  // Handle key input (auto-uppercase)
  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    setNewKey(value);
    setKeyError('');
  };

  return (
    <div className="space-y-4">
      {/* Current Variables */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Environment Variables ({variables.length})
        </label>
        {variables.length > 0 ? (
          <div className="border border-gray-300 rounded-md divide-y divide-gray-200">
            {variables.map((variable) => (
              <div
                key={variable.key}
                className="flex items-start gap-3 p-3 bg-white first:rounded-t-md last:rounded-b-md"
              >
                <div className="flex-shrink-0 w-40">
                  <span className="font-mono text-sm font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">
                    {variable.key}
                  </span>
                </div>
                <div className="flex-1">
                  {disabled ? (
                    <span className="text-sm text-gray-600 font-mono break-all">
                      {variable.value ? '••••••••' : '(empty)'}
                    </span>
                  ) : (
                    <Input
                      type="text"
                      value={variable.value}
                      onChange={(e) => updateVariable(variable.key, e.target.value)}
                      placeholder="Enter value..."
                      disabled={disabled}
                      className="font-mono text-sm"
                    />
                  )}
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeVariable(variable.key)}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    aria-label={`Remove ${variable.key}`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-md p-4 text-center text-gray-500 text-sm">
            No environment variables defined yet.
          </div>
        )}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* Add New Variable */}
      {!disabled && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Add New Variable
          </label>
          <div className="flex gap-3">
            <div className="w-40">
              <Input
                type="text"
                placeholder="KEY_NAME"
                value={newKey}
                onChange={handleKeyChange}
                disabled={disabled}
                className="font-mono text-sm"
                error={!!keyError}
              />
            </div>
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                disabled={disabled}
                className="font-mono text-sm"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addVariable();
                  }
                }}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={addVariable}
              disabled={disabled || !newKey.trim()}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
          {keyError && (
            <p className="mt-2 text-sm text-red-600">{keyError}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Keys must be uppercase (e.g., DATABASE_URL, API_KEY). Values are stored securely.
          </p>
        </div>
      )}

      {/* Common Variables Suggestions */}
      {!disabled && variables.length === 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Common Variables
          </label>
          <div className="flex flex-wrap gap-2">
            {['DATABASE_URL', 'API_KEY', 'SECRET_KEY', 'LOG_LEVEL', 'DEBUG'].map((suggestedKey) => (
              <button
                key={suggestedKey}
                type="button"
                onClick={() => {
                  setNewKey(suggestedKey);
                  // Focus on value input
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-mono text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 transition-colors"
              >
                <PlusIcon className="h-3 w-3" />
                {suggestedKey}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
